import React, { useEffect, useState, useRef } from 'react'
import {
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle,
  Clock,
  Target,
  Hash,
  Layers,
  Users,
  RefreshCw,
  Loader2,
  Briefcase,
  X,
  Search,
  ChevronUp,
  ChevronDown,
  Info
} from 'lucide-react'
import {
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Area,
  ReferenceLine,
  ComposedChart
} from 'recharts'
import { Tooltip } from '../ui'
import useActivationDashboardStore from '../../stores/activationDashboardStore'
import useGlobalCustomerStore from '../../stores/globalCustomerStore'
import { DashboardPeriod, ActiveActivation } from '../../types/activationDashboard'
import { DateRangePicker } from '../ui'
import { getPartnerLogoUrl } from '../../services/partnerService'

// Format EMV with smart formatting and commas
// Values under $100 show 2 decimal places ($2.40, $99.99)
// Values $100+ use compact display ($150, $1.5K, $2.3M)
const formatEmvValue = (value: number): string => {
  if (value === 0) return '$0'
  const absValue = Math.abs(value)
  if (absValue < 100) return `$${value.toFixed(2)}`
  if (absValue < 1000) return `$${Math.round(value).toLocaleString()}`
  if (absValue < 10000) return `$${(value / 1000).toFixed(1)}K`
  if (absValue < 1000000) return `$${Math.round(value / 1000).toLocaleString()}K`
  return `$${(value / 1000000).toFixed(1)}M`
}

// Format EMV gap - always shows full dollar amount with commas
const formatEmvGap = (value: number): string => {
  if (value === 0) return '$0'
  return `$${Math.round(value).toLocaleString()}`
}

// Format EMV per day with proper handling of small values and commas
const formatEmvPerDay = (value: number): string => {
  if (value === 0) return '$0'
  const absValue = Math.abs(value)
  if (absValue < 1) return `$${value.toFixed(2)}`
  if (absValue < 10) return `$${value.toFixed(1)}`
  if (absValue < 1000) return `$${Math.round(value).toLocaleString()}`
  if (absValue < 10000) return `$${(value / 1000).toFixed(1)}K`
  return `$${Math.round(value / 1000).toLocaleString()}K`
}

// Progress Gauge Component
const ProgressGauge: React.FC<{ value: number; target: number; size?: number }> = ({
  value,
  target,
  size = 160
}) => {
  const progress = Math.min((value / target) * 100, 100) || 0
  const radius = size / 2 - 20
  const circumference = Math.PI * radius
  const strokeDashoffset = circumference - (progress / 100) * circumference

  const getColor = (pct: number) => {
    if (pct >= 90) return '#10B981'
    if (pct >= 70) return '#F59E0B'
    return '#EF4444'
  }

  return (
    <div className="relative" style={{ width: size, height: size / 2 + 30 }}>
      <svg width={size} height={size / 2 + 20}>
        <path
          d={`M ${20} ${size / 2} A ${radius} ${radius} 0 0 1 ${size - 20} ${size / 2}`}
          fill="none"
          stroke="#E5E7EB"
          strokeWidth="12"
          strokeLinecap="round"
        />
        <path
          d={`M ${20} ${size / 2} A ${radius} ${radius} 0 0 1 ${size - 20} ${size / 2}`}
          fill="none"
          stroke={getColor(progress)}
          strokeWidth="12"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          style={{ transition: 'stroke-dashoffset 1s ease-out' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-end pb-2">
        <span className="text-2xl font-bold text-gray-900">{progress.toFixed(0)}%</span>
        <span className="text-xs text-gray-500">of target</span>
      </div>
    </div>
  )
}

// Status Badge Component
const StatusBadge: React.FC<{ status: 'on-track' | 'at-risk' | 'behind' }> = ({ status }) => {
  const configs = {
    'on-track': {
      icon: CheckCircle,
      color: 'text-emerald-600',
      bg: 'bg-emerald-50',
      label: 'On Track'
    },
    'at-risk': {
      icon: AlertTriangle,
      color: 'text-amber-600',
      bg: 'bg-amber-50',
      label: 'At Risk'
    },
    behind: { icon: Clock, color: 'text-red-600', bg: 'bg-red-50', label: 'Behind' }
  }
  const config = configs[status]
  const Icon = config.icon

  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color} ${config.bg}`}
    >
      <Icon size={12} />
      {config.label}
    </span>
  )
}

// Progress Bar Component
const ProgressBar: React.FC<{ value: number; max: number; color?: string; gradient?: string }> = ({
  value,
  max,
  color = '#8B5CF6',
  gradient
}) => {
  const pct = Math.min((value / max) * 100, 100) || 0
  return (
    <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
      <div
        className="h-full rounded-full transition-all duration-500"
        style={{
          width: `${pct}%`,
          background: gradient || color
        }}
      />
    </div>
  )
}

// Mini Donut Component
const MiniDonut: React.FC<{
  data: Array<{ name: string; value: number; color: string }>
  size?: number
}> = ({ data, size = 100 }) => {
  const total = data.reduce((sum, d) => sum + d.value, 0)

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <ResponsiveContainer>
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            innerRadius={size * 0.32}
            outerRadius={size * 0.45}
            paddingAngle={2}
            dataKey="value"
          >
            {data.map((entry, index) => (
              <Cell key={index} fill={entry.color} />
            ))}
          </Pie>
        </PieChart>
      </ResponsiveContainer>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-sm font-bold text-gray-900">
          {formatEmvValue(total)}
        </span>
        <span className="text-[10px] text-gray-500">EMV</span>
      </div>
    </div>
  )
}

// Card Component with hover animation
const Card: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className = ''
}) => (
  <div className={`bg-white rounded-xl shadow-sm border border-gray-200 p-4 hover:shadow-lg hover:border-gray-300 hover:-translate-y-0.5 transition-all duration-300 ${className}`}>
    {children}
  </div>
)

// Mini Health Donut for Activation Status
const HealthDonut: React.FC<{
  onTrack: number
  atRisk: number
  behind: number
  size?: number
}> = ({ onTrack, atRisk, behind, size = 40 }) => {
  const total = onTrack + atRisk + behind
  if (total === 0) return null

  const data = [
    { value: onTrack, color: '#10B981' },
    { value: atRisk, color: '#F59E0B' },
    { value: behind, color: '#EF4444' }
  ].filter(d => d.value > 0)

  return (
    <div style={{ width: size, height: size }}>
      <ResponsiveContainer>
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            innerRadius={size * 0.3}
            outerRadius={size * 0.48}
            paddingAngle={2}
            dataKey="value"
          >
            {data.map((entry, index) => (
              <Cell key={index} fill={entry.color} />
            ))}
          </Pie>
        </PieChart>
      </ResponsiveContainer>
    </div>
  )
}

// Asset EMV Modal Component
const AssetEmvModal: React.FC<{
  isOpen: boolean
  onClose: () => void
  assets: Array<{ name: string; value: number; postCount: number; color: string }>
  formatEmvValue: (value: number) => string
}> = ({ isOpen, onClose, assets, formatEmvValue }) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [sortField, setSortField] = useState<'name' | 'value' | 'postCount'>('value')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc')

  if (!isOpen) return null

  const filteredAssets = assets.filter(a =>
    a.name.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const sortedAssets = [...filteredAssets].sort((a, b) => {
    const multiplier = sortDirection === 'asc' ? 1 : -1
    if (sortField === 'name') {
      return multiplier * a.name.localeCompare(b.name)
    }
    return multiplier * (a[sortField] - b[sortField])
  })

  const handleSort = (field: 'name' | 'value' | 'postCount') => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection(field === 'name' ? 'asc' : 'desc')
    }
  }

  const SortIcon = ({ field }: { field: 'name' | 'value' | 'postCount' }) => {
    if (sortField !== field) return null
    return sortDirection === 'asc' ? <ChevronUp size={10} /> : <ChevronDown size={10} />
  }

  const total = assets.reduce((sum, a) => sum + a.value, 0)
  const totalPosts = assets.reduce((sum, a) => sum + a.postCount, 0)
  const maxValue = assets.length > 0 ? Math.max(...assets.map(a => a.value)) : 1

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-8 py-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">EMV by Asset</h3>
            <p className="text-xs text-gray-500">
              {assets.length} assets · {formatEmvValue(total)} · {totalPosts.toLocaleString()} posts
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        {/* Search & Sort */}
        <div className="px-5 py-3 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search assets..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-7 pr-7 py-1 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X size={12} />
                </button>
              )}
            </div>
            <div className="flex items-center gap-1 text-[10px] text-gray-500">
              <span>Sort:</span>
              <button
                onClick={() => handleSort('name')}
                className={`px-1.5 py-0.5 rounded flex items-center gap-0.5 ${sortField === 'name' ? 'bg-gray-200 text-gray-700' : 'hover:bg-gray-100'}`}
              >
                Name <SortIcon field="name" />
              </button>
              <button
                onClick={() => handleSort('value')}
                className={`px-1.5 py-0.5 rounded flex items-center gap-0.5 ${sortField === 'value' ? 'bg-gray-200 text-gray-700' : 'hover:bg-gray-100'}`}
              >
                EMV <SortIcon field="value" />
              </button>
              <button
                onClick={() => handleSort('postCount')}
                className={`px-1.5 py-0.5 rounded flex items-center gap-0.5 ${sortField === 'postCount' ? 'bg-gray-200 text-gray-700' : 'hover:bg-gray-100'}`}
              >
                Posts <SortIcon field="postCount" />
              </button>
            </div>
          </div>
        </div>

        {/* Scrollable List - Card Style */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          {sortedAssets.length > 0 ? (
            sortedAssets.map((asset, i) => (
              <div key={i} className="space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-gray-700 text-xs truncate flex-1" title={asset.name}>
                    {asset.name}
                  </span>
                  <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                    <span className="text-gray-400 text-[10px]">
                      {asset.postCount.toLocaleString()} posts
                    </span>
                    <span className="text-gray-900 text-xs font-medium">
                      {formatEmvValue(asset.value)}
                    </span>
                  </div>
                </div>
                <ProgressBar
                  value={asset.value}
                  max={maxValue}
                  gradient={`linear-gradient(90deg, ${asset.color}66 0%, ${asset.color}99 50%, ${asset.color} 100%)`}
                />
              </div>
            ))
          ) : (
            <div className="flex items-center justify-center py-8 text-gray-400 text-xs">
              No assets found
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// Tags Modal Component
const TagsModal: React.FC<{
  isOpen: boolean
  onClose: () => void
  tags: Array<{
    hashtagId: number
    hashtagText: string
    emv: number
    postCount: number
    trend: number
    trendDirection: 'up' | 'down' | 'flat'
  }>
  formatEmvValue: (value: number) => string
}> = ({ isOpen, onClose, tags, formatEmvValue }) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [sortField, setSortField] = useState<'hashtagText' | 'emv' | 'postCount'>('emv')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc')

  if (!isOpen) return null

  const filteredTags = tags.filter(t =>
    t.hashtagText.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const sortedTags = [...filteredTags].sort((a, b) => {
    const multiplier = sortDirection === 'asc' ? 1 : -1
    if (sortField === 'hashtagText') {
      return multiplier * a.hashtagText.localeCompare(b.hashtagText)
    }
    return multiplier * (a[sortField] - b[sortField])
  })

  const handleSort = (field: 'hashtagText' | 'emv' | 'postCount') => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection(field === 'hashtagText' ? 'asc' : 'desc')
    }
  }

  const SortIcon = ({ field }: { field: 'hashtagText' | 'emv' | 'postCount' }) => {
    if (sortField !== field) return null
    return sortDirection === 'asc' ? <ChevronUp size={10} /> : <ChevronDown size={10} />
  }

  const totalEmv = tags.reduce((sum, t) => sum + t.emv, 0)
  const totalPosts = tags.reduce((sum, t) => sum + t.postCount, 0)
  const maxEmv = tags.length > 0 ? Math.max(...tags.map(t => t.emv)) : 1

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-8 py-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Attribution Tags</h3>
            <p className="text-xs text-gray-500">
              {tags.length} tags · {formatEmvValue(totalEmv)} · {totalPosts.toLocaleString()} posts
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        {/* Search & Sort */}
        <div className="px-5 py-3 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search tags..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-7 pr-7 py-1 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X size={12} />
                </button>
              )}
            </div>
            <div className="flex items-center gap-1 text-[10px] text-gray-500">
              <span>Sort:</span>
              <button
                onClick={() => handleSort('hashtagText')}
                className={`px-1.5 py-0.5 rounded flex items-center gap-0.5 ${sortField === 'hashtagText' ? 'bg-gray-200 text-gray-700' : 'hover:bg-gray-100'}`}
              >
                Name <SortIcon field="hashtagText" />
              </button>
              <button
                onClick={() => handleSort('emv')}
                className={`px-1.5 py-0.5 rounded flex items-center gap-0.5 ${sortField === 'emv' ? 'bg-gray-200 text-gray-700' : 'hover:bg-gray-100'}`}
              >
                EMV <SortIcon field="emv" />
              </button>
              <button
                onClick={() => handleSort('postCount')}
                className={`px-1.5 py-0.5 rounded flex items-center gap-0.5 ${sortField === 'postCount' ? 'bg-gray-200 text-gray-700' : 'hover:bg-gray-100'}`}
              >
                Posts <SortIcon field="postCount" />
              </button>
            </div>
          </div>
        </div>

        {/* Scrollable List */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          {sortedTags.length > 0 ? (
            sortedTags.map((tag) => (
              <div key={tag.hashtagId} className="space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-gray-700 text-xs truncate flex-1" title={tag.hashtagText.startsWith('@') || tag.hashtagText.startsWith('#') ? tag.hashtagText : `#${tag.hashtagText}`}>
                    {tag.hashtagText.startsWith('@') || tag.hashtagText.startsWith('#') ? tag.hashtagText : `#${tag.hashtagText}`}
                  </span>
                  <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                    <span className="text-gray-400 text-[10px]">
                      {tag.postCount.toLocaleString()} posts
                    </span>
                    <span
                      className={`text-[10px] flex items-center gap-0.5 ${
                        tag.trendDirection === 'up'
                          ? 'text-emerald-600'
                          : tag.trendDirection === 'down'
                            ? 'text-red-600'
                            : 'text-gray-500'
                      }`}
                    >
                      {tag.trendDirection === 'up' ? (
                        <TrendingUp size={10} />
                      ) : tag.trendDirection === 'down' ? (
                        <TrendingDown size={10} />
                      ) : null}
                      {Math.abs(tag.trend)}%
                    </span>
                    <span className="text-gray-900 text-xs font-medium">
                      {formatEmvValue(tag.emv)}
                    </span>
                  </div>
                </div>
                <ProgressBar
                  value={tag.emv}
                  max={maxEmv}
                  gradient="linear-gradient(90deg, #8B5CF666 0%, #8B5CF699 50%, #8B5CF6 100%)"
                />
              </div>
            ))
          ) : (
            <div className="flex items-center justify-center py-8 text-gray-400 text-xs">
              No tags found
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// Main Dashboard Component
const ActivationDashboard: React.FC = () => {
  const { selectedCustomer } = useGlobalCustomerStore()
  const {
    summary,
    emvTrend,
    accountEmv,
    assetTypeEmv,
    assetEmv,
    topTags,
    activeActivations,
    loadingAll,
    error,
    period,
    customDateRange,
    setPeriod,
    setCustomDateRange,
    loadAllDashboardData,
    loadActiveActivations
  } = useActivationDashboardStore()

  const [isRefreshing, setIsRefreshing] = useState(false)
  const [isAssetModalOpen, setIsAssetModalOpen] = useState(false)
  const [isAssetListExpanded, setIsAssetListExpanded] = useState(false)
  const [isTagsListExpanded, setIsTagsListExpanded] = useState(false)
  const [isTagsModalOpen, setIsTagsModalOpen] = useState(false)
  const [initialLoadDone, setInitialLoadDone] = useState(false)
  const lastCustomerIdRef = useRef<string | null>(null)
  const skipNextPeriodChangeRef = useRef(false)

  const customerId = selectedCustomer?.id ? String(selectedCustomer.id) : ''

  // Initial load: compute date range from active activations
  useEffect(() => {
    const initializeDashboard = async () => {
      if (!customerId) return

      // Reset initial load when customer changes
      const customerChanged = customerId !== lastCustomerIdRef.current
      if (customerChanged) {
        lastCustomerIdRef.current = customerId
        setInitialLoadDone(false)
      }

      // On initial load for this customer, compute date range from active activations
      if (!initialLoadDone || customerChanged) {
        try {
          // Load active activations to get earliest start_date
          await loadActiveActivations(customerId)

          // Get the activations from the store (they'll be loaded by now)
          const store = useActivationDashboardStore.getState()
          const activations = store.activeActivations?.activations || []

          if (activations.length > 0) {
            // Find earliest start_date from active activations
            const earliestDate = activations.reduce((earliest, activation) => {
              const startDate = new Date(activation.startDate)
              return startDate < earliest ? startDate : earliest
            }, new Date(activations[0].startDate))

            // Set custom date range from earliest activation to today
            const today = new Date()
            const dateRange = {
              start: earliestDate.toISOString().split('T')[0],
              end: today.toISOString().split('T')[0]
            }

            // Skip the next period change effect since we're setting it programmatically
            skipNextPeriodChangeRef.current = true

            // Set custom period and date range
            setCustomDateRange(dateRange)
            setPeriod('custom')

            // Load rest of dashboard data with this range
            await loadAllDashboardData(customerId, 'custom', dateRange)
          } else {
            // No active activations, fall back to 90 days
            await loadAllDashboardData(customerId, '90days', null)
          }

          setInitialLoadDone(true)
        } catch (error) {
          console.error('Failed to initialize dashboard:', error)
          // Fall back to default behavior on error
          await loadAllDashboardData(customerId, '90days', null)
          setInitialLoadDone(true)
        }
      }
    }

    initializeDashboard()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId])

  // Reload when user changes period/date range (after initial load)
  useEffect(() => {
    // Skip if this is from the initial load setting the period
    if (skipNextPeriodChangeRef.current) {
      skipNextPeriodChangeRef.current = false
      return
    }

    if (customerId && initialLoadDone) {
      loadAllDashboardData(customerId, period, customDateRange)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, customDateRange])

  const handleDateRangeChange = (range: { start: string; end: string } | null, newPeriod: string) => {
    if (newPeriod === 'custom' && range) {
      setCustomDateRange(range)
      setPeriod('custom')
    } else {
      setCustomDateRange(null)
      setPeriod(newPeriod as DashboardPeriod)
    }
  }

  const handleRefresh = async () => {
    if (!customerId) return
    setIsRefreshing(true)
    await loadAllDashboardData(customerId, period, customDateRange)
    setIsRefreshing(false)
  }

  if (!customerId) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500">
        Please select a customer to view the dashboard
      </div>
    )
  }

  if (loadingAll && !summary) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
        <span className="ml-2 text-gray-500">Loading dashboard...</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-red-500">
        <AlertTriangle className="h-8 w-8 mb-2" />
        <p>{error}</p>
        <button
          onClick={handleRefresh}
          className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
        >
          Retry
        </button>
      </div>
    )
  }

  // Format data for charts
  const trendChartData =
    emvTrend?.trendData.map((d) => ({
      date: new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      emv: d.deliveredEmv,
      target: d.targetEmv
    })) || []

  // Account EMV data - show account info with profile pics
  const accountChartData =
    accountEmv?.accounts.map((a) => ({
      name: a.username,
      displayName: a.displayName,
      profilePicUrl: a.profilePicUrl,
      platformName: a.platformName,
      value: a.emv,
      color: a.color
    })) || []

  const assetChartData =
    assetTypeEmv?.breakdown.map((a) => ({
      name: a.name,
      value: a.emv,
      color: a.color
    })) || []

  // Individual asset EMV data
  const assetEmvChartData =
    assetEmv?.assets.map((a) => ({
      name: a.assetName,
      value: a.emv,
      postCount: a.postCount,
      color: a.color
    })) || []

  const activationsList = activeActivations?.activations || []
  const activationSummary = activeActivations?.summary || { total: 0, onTrack: 0, atRisk: 0, behind: 0 }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Activation Performance</h2>
          <p className="text-gray-500 text-sm">
            Real-time view of active campaigns and EMV delivery
          </p>
        </div>
        <div className="flex items-center gap-4">
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          </button>
          <DateRangePicker
            value={customDateRange}
            onChange={handleDateRangeChange}
            maxRetentionDays={90}
            disabled={!customerId}
            selectedPeriod={period}
          />
        </div>
      </div>

      {/* Top Row: Overall Progress, EMV by Account, EMV by Format */}
      <div className="grid grid-cols-3 gap-4">
        {/* Overall Progress Gauge */}
        <Card className="flex flex-col items-center">
          <div className="flex items-center gap-2 text-gray-600 text-sm mb-2">
            <Target size={14} />
            <span className="font-semibold">Overall Progress</span>
            <Tooltip
              content="Total EMV delivered across all active activations vs. combined target. Each post's EMV is counted once, even if it matches multiple assets."
              placement="top"
              maxWidth={240}
            >
              <Info className="h-3.5 w-3.5 text-gray-400 hover:text-gray-600 cursor-help" />
            </Tooltip>
          </div>
          <ProgressGauge
            value={summary?.totalDeliveredEmv || 0}
            target={summary?.totalTargetEmv || 1}
          />
          <div className="text-center mt-2">
            <span className="text-gray-500 text-xs">
              {formatEmvValue(summary?.totalDeliveredEmv || 0)} of{' '}
              {formatEmvValue(summary?.totalTargetEmv || 0)}
            </span>
          </div>
        </Card>

        {/* EMV by Account */}
        <Card>
          <div className="flex items-center gap-2 text-gray-600 text-sm mb-2">
            <Users size={14} />
            <span className="font-semibold">EMV by Account</span>
            <Tooltip
              content="EMV distribution across social media accounts. Shows which accounts are generating the most value from matched posts."
              placement="top"
              maxWidth={220}
            >
              <Info className="h-3.5 w-3.5 text-gray-400 hover:text-gray-600 cursor-help" />
            </Tooltip>
          </div>
          {accountChartData.length > 0 ? (
            <div className="flex items-center gap-3">
              <MiniDonut data={accountChartData} size={100} />
              <div className="flex-1 space-y-1.5">
                {accountChartData.slice(0, 4).map((item, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    {item.profilePicUrl ? (
                      <img
                        src={item.profilePicUrl}
                        alt=""
                        className="w-4 h-4 rounded-full object-cover"
                      />
                    ) : (
                      <div
                        className="w-4 h-4 rounded-full"
                        style={{ backgroundColor: item.color }}
                      />
                    )}
                    <span className="text-gray-700 flex-1 truncate" title={item.displayName || item.name}>
                      @{item.name}
                    </span>
                    <span className="text-gray-500">{formatEmvValue(item.value)}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center text-gray-400 text-sm py-8">No data available</div>
          )}
        </Card>

        {/* EMV by Format */}
        <Card>
          <div className="flex items-center gap-2 text-gray-600 text-sm mb-2">
            <Layers size={14} />
            <span className="font-semibold">EMV by Format</span>
            <Tooltip
              content="EMV breakdown by content format (Reels, Stories, Posts, etc.). Shows which formats are driving the most value."
              placement="top"
              maxWidth={220}
            >
              <Info className="h-3.5 w-3.5 text-gray-400 hover:text-gray-600 cursor-help" />
            </Tooltip>
          </div>
          {assetChartData.length > 0 ? (
            <div className="flex items-center gap-3">
              <MiniDonut data={assetChartData} size={100} />
              <div className="flex-1 space-y-1.5">
                {assetChartData.slice(0, 4).map((item, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    <div
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: item.color }}
                    />
                    <span className="text-gray-700 flex-1 truncate">{item.name}</span>
                    <span className="text-gray-500">{formatEmvValue(item.value)}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center text-gray-400 text-sm py-8">No data available</div>
          )}
        </Card>
      </div>

      {/* Second Row: EMV Delivery Chart and EMV by Asset */}
      <div className="grid grid-cols-2 gap-4">
        {/* EMV Trend Chart */}
        <Card>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2 text-gray-600 text-sm">
              <TrendingUp size={14} />
              <span className="font-semibold">EMV Delivery vs Target</span>
              <Tooltip
                content="Daily EMV delivered compared to the daily target needed to hit your goal. The dashed line shows the pace you need to maintain."
                placement="top"
                maxWidth={240}
              >
                <Info className="h-3.5 w-3.5 text-gray-400 hover:text-gray-600 cursor-help" />
              </Tooltip>
            </div>
            <div className="flex items-center gap-4 text-xs">
              <div className="flex items-center gap-2">
                <div className="w-3 h-0.5 bg-violet-500 rounded" />
                <span className="text-gray-500">Delivered</span>
              </div>
              <div className="flex items-center gap-2">
                <div
                  className="w-3 h-0.5 rounded"
                  style={{ borderTop: '2px dashed #9CA3AF' }}
                />
                <span className="text-gray-500">Daily Target</span>
              </div>
            </div>
          </div>
          <div className="h-48">
            {trendChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={trendChartData}>
                  <defs>
                    <linearGradient id="emvAreaGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis
                    dataKey="date"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#6B7280', fontSize: 11 }}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#6B7280', fontSize: 11 }}
                    tickFormatter={(v) => formatEmvValue(v)}
                  />
                  <RechartsTooltip
                    contentStyle={{
                      backgroundColor: '#fff',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      fontSize: '12px',
                      padding: '8px 12px'
                    }}
                    labelStyle={{
                      fontSize: '11px',
                      color: '#6B7280',
                      marginBottom: '4px'
                    }}
                    formatter={(value: number, name: string) => [
                      `$${value.toLocaleString()}`,
                      name === 'emv' ? 'Delivered' : 'Target'
                    ]}
                  />
                  {/* Target reference line at daily target value */}
                  {trendChartData.length > 0 && trendChartData[0]?.target > 0 && (
                    <ReferenceLine
                      y={trendChartData[0].target}
                      stroke="#9CA3AF"
                      strokeDasharray="4 4"
                      strokeWidth={1.5}
                    />
                  )}
                  <Area
                    type="monotone"
                    dataKey="emv"
                    stroke="#8B5CF6"
                    strokeWidth={2}
                    fill="url(#emvAreaGradient)"
                  />
                </ComposedChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-400 text-sm">
                No trend data available
              </div>
            )}
          </div>
        </Card>

        {/* EMV by Asset - Stacked layout with name above progress bar */}
        <Card>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 text-gray-600 text-sm">
              <Briefcase size={14} />
              <span className="font-semibold">EMV by Asset</span>
              <Tooltip
                content="EMV attributed to each asset. Posts matching multiple assets are credited to each, so the sum may exceed Total EMV."
                placement="top"
                maxWidth={240}
              >
                <Info className="h-3.5 w-3.5 text-gray-400 hover:text-gray-600 cursor-help" />
              </Tooltip>
            </div>
            {assetEmvChartData.length > 0 && (
              <span className="text-xs text-gray-500">
                Total: {formatEmvValue(assetEmvChartData.reduce((sum, a) => sum + a.value, 0))}
              </span>
            )}
          </div>
          {assetEmvChartData.length > 0 ? (
            <div className="space-y-3">
              {(isAssetListExpanded ? assetEmvChartData : assetEmvChartData.slice(0, 5)).map((item, i) => (
                <div key={i} className="space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-700 text-xs truncate flex-1" title={item.name}>
                      {item.name}
                    </span>
                    <span className="text-gray-900 text-xs font-medium flex-shrink-0 ml-2">
                      {formatEmvValue(item.value)}
                    </span>
                  </div>
                  <ProgressBar
                    value={item.value}
                    max={assetEmvChartData[0]?.value || 1}
                    gradient={`linear-gradient(90deg, ${item.color}66 0%, ${item.color}99 50%, ${item.color} 100%)`}
                  />
                </div>
              ))}
              {/* Expand/collapse toggle */}
              {assetEmvChartData.length > 5 && (
                <button
                  onClick={() => setIsAssetListExpanded(!isAssetListExpanded)}
                  className="flex items-center justify-center w-full pt-2 border-t border-gray-100 text-xs text-gray-500 hover:text-gray-700 transition-colors"
                >
                  <span className="mr-1">
                    {isAssetListExpanded ? 'Show less' : `+${assetEmvChartData.length - 5} more assets`}
                  </span>
                  {isAssetListExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </button>
              )}
            </div>
          ) : (
            <div className="text-center text-gray-400 text-sm py-8">No data available</div>
          )}
        </Card>
      </div>

      {/* Third Row: EMV Pace and Top Attribution Tags */}
      <div className="grid grid-cols-4 gap-4">
        {/* EMV Pace */}
        <Card className="col-span-1">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 text-gray-600 text-sm">
              <TrendingUp size={14} />
              <span className="font-semibold">EMV Pace</span>
              <Tooltip
                content="Current daily EMV rate vs. the rate needed to hit your target. Projected EMV shows where you'll land at the current pace."
                placement="top"
                maxWidth={220}
              >
                <Info className="h-3.5 w-3.5 text-gray-400 hover:text-gray-600 cursor-help" />
              </Tooltip>
            </div>
            {summary?.velocity && (
              <span
                className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                  summary.velocity.paceStatus === 'ahead'
                    ? 'bg-emerald-50 text-emerald-600'
                    : summary.velocity.paceStatus === 'on-track'
                      ? 'bg-emerald-50 text-emerald-600'
                      : summary.velocity.paceStatus === 'tight'
                        ? 'bg-amber-50 text-amber-600'
                        : 'bg-red-50 text-red-600'
                }`}
              >
                {summary.velocity.paceStatus === 'ahead'
                  ? 'Ahead'
                  : summary.velocity.paceStatus === 'on-track'
                    ? 'On Track'
                    : summary.velocity.paceStatus === 'tight'
                      ? 'Tight'
                      : 'Behind'}
              </span>
            )}
          </div>

          {summary?.velocity && (
            <>
              <div className="grid grid-cols-2 gap-4 mb-3">
                <div>
                  <span className="text-gray-500 text-xs">Current</span>
                  <div className="text-gray-900 text-sm font-medium mt-0.5">
                    {formatEmvPerDay(summary.velocity.currentVelocity)}
                    <span className="text-gray-400 text-xs">/day</span>
                  </div>
                </div>
                <div>
                  <span className="text-gray-500 text-xs">Needed</span>
                  <div
                    className={`text-sm font-medium mt-0.5 ${
                      summary.velocity.emvGap >= 0
                        ? 'text-emerald-600'
                        : summary.velocity.requiredVelocity > summary.velocity.currentVelocity
                          ? 'text-amber-600'
                          : 'text-emerald-600'
                    }`}
                  >
                    {summary.velocity.emvGap >= 0 ? '$0' : formatEmvPerDay(summary.velocity.requiredVelocity)}
                    <span className="text-gray-400 text-xs">/day</span>
                  </div>
                </div>
              </div>

              <div className="border-t border-gray-100 my-3" />

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-gray-500 text-xs">Projected</span>
                  <div className="text-gray-900 text-lg font-bold mt-0.5">
                    {formatEmvValue(summary.velocity.projectedEmv)}
                  </div>
                </div>
                <div>
                  <span className="text-gray-500 text-xs">Gap</span>
                  <div
                    className={`text-sm font-medium flex items-center gap-1 mt-0.5 ${
                      summary.velocity.emvGap >= 0 ? 'text-emerald-600' : 'text-red-600'
                    }`}
                  >
                    {summary.velocity.emvGap >= 0 ? (
                      <TrendingUp size={12} />
                    ) : (
                      <TrendingDown size={12} />
                    )}
                    {summary.velocity.emvGap >= 0 ? '+' : ''}
                    {formatEmvGap(summary.velocity.emvGap)}
                  </div>
                </div>
              </div>

              <div className="mt-3 pt-2 border-t border-gray-50">
                <span className="text-gray-400 text-xs">
                  {summary.velocity.daysRemaining} days remaining
                </span>
              </div>
            </>
          )}
        </Card>

        {/* Top Attribution Tags */}
        <Card className="col-span-3">
        <div className="flex items-center gap-2 text-gray-600 text-sm mb-4">
          <Hash size={14} />
          <span className="font-semibold">Top Attribution Tags</span>
          <Tooltip
            content="Attribution tags ranked by EMV. Tags used on multiple posts accumulate their EMV. Trend shows change vs. previous period."
            placement="top"
            maxWidth={240}
          >
            <Info className="h-3.5 w-3.5 text-gray-400 hover:text-gray-600 cursor-help" />
          </Tooltip>
        </div>
        {topTags?.tags && topTags.tags.length > 0 ? (
          <>
            <div className="grid grid-cols-4 gap-4">
              {(isTagsListExpanded ? topTags.tags : topTags.tags.slice(0, 8)).map((tag, i) => (
                <div key={tag.hashtagId} className="flex items-center gap-3">
                  <span className="text-gray-400 text-xs w-4">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-gray-900 text-xs font-medium truncate">
                        {tag.hashtagText.startsWith('@') || tag.hashtagText.startsWith('#') ? tag.hashtagText : `#${tag.hashtagText}`}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-900 font-bold">
                        {formatEmvValue(tag.emv)}
                      </span>
                      <span className="text-gray-500">
                        {tag.postCount.toLocaleString()} posts
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <div className="flex-1">
                        <ProgressBar
                          value={tag.emv}
                          max={topTags.tags[0]?.emv || 1}
                          color="#8B5CF6"
                        />
                      </div>
                      <span
                        className={`text-xs flex items-center gap-0.5 ${
                          tag.trendDirection === 'up'
                            ? 'text-emerald-600'
                            : tag.trendDirection === 'down'
                              ? 'text-red-600'
                              : 'text-gray-500'
                        }`}
                      >
                        {tag.trendDirection === 'up' ? (
                          <TrendingUp size={10} />
                        ) : tag.trendDirection === 'down' ? (
                          <TrendingDown size={10} />
                        ) : null}
                        {Math.abs(tag.trend)}%
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {topTags.tags.length > 8 && (
              <button
                onClick={() => setIsTagsListExpanded(!isTagsListExpanded)}
                className="flex items-center justify-center w-full pt-3 mt-3 border-t border-gray-100 text-xs text-gray-500 hover:text-gray-700 transition-colors"
              >
                <span className="mr-1">
                  {isTagsListExpanded ? 'Show less' : `+${topTags.tags.length - 8} more tags`}
                </span>
                {isTagsListExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>
            )}
          </>
        ) : (
          <div className="text-center text-gray-400 text-sm py-8">No tags data available</div>
        )}
        </Card>
      </div>

      {/* Bottom: Active Activations Table */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3 text-gray-500 text-sm">
            <HealthDonut
              onTrack={activationSummary.onTrack}
              atRisk={activationSummary.atRisk}
              behind={activationSummary.behind}
              size={36}
            />
            <div>
              <div className="flex items-center gap-2">
                <Target size={14} />
                <span className="font-medium text-gray-700">Active Activations</span>
                <Tooltip
                  content="All currently active activations with their EMV progress. Status is based on delivery pace vs. time remaining."
                  placement="top"
                  maxWidth={240}
                >
                  <Info className="h-3.5 w-3.5 text-gray-400 hover:text-gray-600 cursor-help" />
                </Tooltip>
                <span className="bg-violet-100 text-violet-600 px-2 py-0.5 rounded-full text-xs font-medium">
                  {activationSummary.total} active
                </span>
              </div>
            </div>
          </div>
          <div className="flex gap-4">
            <span className="text-xs text-gray-500 flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-emerald-500" /> On Track:{' '}
              {activationSummary.onTrack}
            </span>
            <span className="text-xs text-gray-500 flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-amber-500" /> At Risk:{' '}
              {activationSummary.atRisk}
            </span>
            <span className="text-xs text-gray-500 flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-red-500" /> Behind:{' '}
              {activationSummary.behind}
            </span>
          </div>
        </div>

        {activationsList.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left text-gray-500 text-xs font-medium py-3 px-2">
                    Activation
                  </th>
                  <th className="text-left text-gray-500 text-xs font-medium py-3 px-2">
                    Status
                  </th>
                  <th className="text-left text-gray-500 text-xs font-medium py-3 px-2">
                    EMV Progress
                  </th>
                  <th className="text-left text-gray-500 text-xs font-medium py-3 px-2">
                    Posts
                  </th>
                  <th className="text-right text-gray-500 text-xs font-medium py-3 px-2">
                    Days Left
                  </th>
                </tr>
              </thead>
              <tbody>
                {activationsList.map((activation: ActiveActivation) => {
                  const emvPct = (activation.deliveredEmv / activation.targetEmv) * 100 || 0
                  const statusColor =
                    activation.status === 'on-track'
                      ? '#10B981'
                      : activation.status === 'at-risk'
                        ? '#F59E0B'
                        : '#EF4444'
                  // Use proxy URL for partner logo to avoid Azure Storage access issues
                  const partnerLogoProxyUrl = activation.partnerId && activation.partnerLogoUrl
                    ? getPartnerLogoUrl(activation.partnerId, customerId)
                    : null

                  return (
                    <tr
                      key={activation.id}
                      className="border-b border-gray-50 hover:bg-gray-50 transition-colors"
                    >
                      <td className="py-3 px-2">
                        <div className="flex items-center gap-2">
                          {partnerLogoProxyUrl ? (
                            <img
                              src={partnerLogoProxyUrl}
                              alt=""
                              className="w-6 h-6 rounded object-contain"
                            />
                          ) : (
                            <div className="w-6 h-6 bg-gray-200 rounded flex items-center justify-center flex-shrink-0">
                              <span className="text-gray-500 text-xs font-semibold">
                                {activation.partnerName?.charAt(0).toUpperCase() || '?'}
                              </span>
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <span className="text-gray-900 text-sm font-medium">
                              {activation.name}
                            </span>
                            {activation.description && (
                              <div className="text-gray-500 text-xs truncate max-w-[200px]" title={activation.description}>
                                {activation.description}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-2">
                        <StatusBadge status={activation.status} />
                      </td>
                      <td className="py-3 px-2">
                        <div className="min-w-[180px]">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                              <div
                                className="h-full rounded-full transition-all duration-500"
                                style={{
                                  width: `${Math.min(emvPct, 100)}%`,
                                  backgroundColor: statusColor
                                }}
                              />
                            </div>
                            <span
                              className={`text-xs font-medium w-10 text-right ${
                                emvPct >= 80
                                  ? 'text-emerald-600'
                                  : emvPct >= 50
                                    ? 'text-amber-600'
                                    : 'text-red-600'
                              }`}
                            >
                              {emvPct.toFixed(0)}%
                            </span>
                          </div>
                          <div className="flex justify-between mt-1">
                            <span className="text-[10px] text-gray-600 font-medium">
                              {formatEmvValue(activation.deliveredEmv)}
                            </span>
                            <span className="text-[10px] text-gray-400">
                              {formatEmvValue(activation.targetEmv)}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-2">
                        <div className="flex items-center gap-2">
                          <div className="w-16">
                            <ProgressBar
                              value={activation.deliveredPosts}
                              max={activation.targetPosts || 1}
                              color="#8B5CF6"
                            />
                          </div>
                          <span className="text-gray-500 text-xs">
                            {activation.deliveredPosts}/{activation.targetPosts}
                          </span>
                        </div>
                      </td>
                      <td className="py-3 px-2 text-right">
                        <span
                          className={`text-sm ${
                            activation.daysRemaining <= 7 ? 'text-amber-600 font-medium' : 'text-gray-700'
                          }`}
                        >
                          {activation.daysRemaining}d
                        </span>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center text-gray-400 text-sm py-8">No active activations</div>
        )}
      </Card>

      {/* Footer */}
      <div className="text-center text-gray-400 text-xs">
        Period: {summary?.periodStart} to {summary?.periodEnd}
      </div>

      {/* Asset EMV Modal */}
      <AssetEmvModal
        isOpen={isAssetModalOpen}
        onClose={() => setIsAssetModalOpen(false)}
        assets={assetEmvChartData}
        formatEmvValue={formatEmvValue}
      />

      <TagsModal
        isOpen={isTagsModalOpen}
        onClose={() => setIsTagsModalOpen(false)}
        tags={topTags?.tags || []}
        formatEmvValue={formatEmvValue}
      />
    </div>
  )
}

export default ActivationDashboard
